<template>
  <div
    :class="[
      'item-name-search',
      { 'item-name-search--portrait': portrait, 'item-name-search--fallback': fallback }
    ]"
  >
    <input
      ref="inputEl"
      :value="modelValue"
      class="item-name-search__input"
      type="text"
      :placeholder="placeholder"
      :required="required"
      autocomplete="off"
      role="combobox"
      aria-autocomplete="list"
      :aria-controls="listboxId"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-activedescendant="activeIndex >= 0 ? optionId(activeIndex) : undefined"
      @focus="openDropdown"
      @input="handleInput"
      @blur="closeDropdown"
      @keydown.down.prevent="moveActive(1)"
      @keydown.up.prevent="moveActive(-1)"
      @keydown.enter="selectActive"
      @keydown.esc.prevent="open = false"
    >

    <button
      v-if="modelValue"
      class="item-name-search__clear"
      type="button"
      aria-label="項目名をクリア"
      @mousedown.prevent
      @click="clearValue"
    >
      ×
    </button>

    <ul
      v-if="open"
      :id="listboxId"
      class="item-name-search__dropdown"
      role="listbox"
      @mousedown.prevent
    >
      <li
        v-for="(suggestion, index) in filteredSuggestions"
        :id="optionId(index)"
        :key="`${suggestion}-${index}`"
        :class="{ 'is-active': index === activeIndex }"
        role="option"
        :aria-selected="index === activeIndex ? 'true' : 'false'"
        @mouseenter="activeIndex = index"
        @mousedown.prevent="selectSuggestion(suggestion)"
      >
        {{ suggestion }}
      </li>
      <li v-if="filteredSuggestions.length === 0" class="item-name-search__empty" role="status">
        候補がありません（入力した内容を使用できます）
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  suggestions: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: '品名・作業名'
  },
  required: {
    type: Boolean,
    default: false
  },
  portrait: {
    type: Boolean,
    default: false
  },
  fallback: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'search']);
const inputEl = ref(null);
const open = ref(false);
const activeIndex = ref(-1);
const listboxId = `estimate-item-name-options-${getCurrentInstance()?.uid}`;
const normalizedText = value => String(value || '').normalize('NFKC').toLowerCase();
const filteredSuggestions = computed(() => {
  const query = normalizedText(props.modelValue).trim();
  const uniqueSuggestions = [...new Set(
    props.suggestions.map(suggestion => String(suggestion || '').trim()).filter(Boolean)
  )];
  if (!query) return uniqueSuggestions;
  return uniqueSuggestions.filter(suggestion => normalizedText(suggestion).includes(query));
});

const optionId = index => `${listboxId}-${index}`;
const openDropdown = () => {
  open.value = true;
  activeIndex.value = -1;
  emit('search', props.modelValue);
};
const handleInput = event => {
  const value = event.target.value.trim();
  emit('update:modelValue', value);
  emit('search', value);
  open.value = true;
  activeIndex.value = -1;
};
const closeDropdown = () => {
  window.setTimeout(() => {
    open.value = false;
    activeIndex.value = -1;
  }, 100);
};
const selectSuggestion = suggestion => {
  emit('update:modelValue', suggestion);
  open.value = false;
  activeIndex.value = -1;
  inputEl.value?.focus();
};
const clearValue = () => {
  emit('update:modelValue', '');
  emit('search', '');
  open.value = true;
  activeIndex.value = -1;
  inputEl.value?.focus();
};
const moveActive = direction => {
  open.value = true;
  const optionCount = filteredSuggestions.value.length;
  if (!optionCount) {
    activeIndex.value = -1;
    return;
  }
  if (activeIndex.value < 0) {
    activeIndex.value = direction > 0 ? 0 : optionCount - 1;
    return;
  }
  activeIndex.value = (activeIndex.value + direction + optionCount) % optionCount;
};
const selectActive = event => {
  const suggestion = filteredSuggestions.value[activeIndex.value];
  if (open.value && suggestion) {
    event.preventDefault();
    selectSuggestion(suggestion);
  }
};

watch(filteredSuggestions, suggestions => {
  if (activeIndex.value >= suggestions.length) activeIndex.value = -1;
});
</script>

<style scoped>
.item-name-search {
  position: relative;
  width: 100%;
  min-width: 0;
  font-family: system-ui, sans-serif;
}

.item-name-search:focus-within {
  z-index: 12;
}

.item-name-search__input {
  width: 100%;
  min-height: 28px;
  padding: 3px 32px 3px 5px;
  border: 1px solid #ced4da;
  border-radius: 2px;
  outline: none;
  background: #fff;
  font: inherit;
}

.item-name-search__input:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.18);
}

.item-name-search--portrait .item-name-search__input {
  min-height: 38px;
  padding: 6px 32px 6px 12px;
  border-radius: 0.375rem;
  font-family: var(--bs-body-font-family);
  font-size: 14px;
}

.item-name-search--fallback {
  margin-top: 2px;
}

.item-name-search--fallback .item-name-search__input {
  font-size: 10px;
}

.item-name-search--portrait.item-name-search--fallback .item-name-search__input {
  font-size: 12px;
}

.item-name-search__clear {
  position: absolute;
  top: 50%;
  right: 7px;
  z-index: 2;
  display: flex;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
  color: #333;
  background: #eee;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
}

.item-name-search__clear:hover {
  color: #000;
  background: #ccc;
}

.item-name-search__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  width: 100%;
  max-height: 240px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  border: 1px solid #ddd;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  list-style: none;
  text-align: left;
}

.item-name-search__dropdown li {
  width: 100%;
  padding: 8px;
  color: #17202a;
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
  overflow-wrap: anywhere;
}

.item-name-search__dropdown li:hover,
.item-name-search__dropdown li.is-active {
  background: #d3e3fd;
}

.item-name-search__dropdown .item-name-search__empty {
  color: #888;
  cursor: default;
  background: #fff;
}
</style>
